import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { getBrowserCookie } from '@backmarket/utils/cookies/getBrowserCookie'
import { isBrowser } from '@backmarket/utils/env/isBrowser'

import { getDeviceId as getAmplitudeDeviceId } from '../amplitude'
import { COOKIES, GDPR_COOKIE_DURATION_IN_MONTHS } from '../constants'
import { createCookieConfig } from '../utils/createCookieConfig'
import { useCookie } from '../utils/useCookie'

import {
  BRAZE_CONSENT_COOKIE,
  BRAZE_SAFARI_PUSH_ID,
  SDK_ENDPOINT,
  SERVICE_WORKER_LOCATION,
} from './constants'

export type BrazeInstance = typeof import('@braze/web-sdk')
let instance = <BrazeInstance>{}

function isInstanceAvailable() {
  return Object.keys(instance).length > 0
}

function isInstanceDisabled() {
  return !isInstanceAvailable() || Boolean(instance?.isDisabled())
}

function hasStoredAmplitudeDeviceId() {
  const localStorage = useLocalStorage()
  const amplitudeDeviceId = localStorage.getItem('amplitudeDeviceId')

  return !!amplitudeDeviceId
}

function setAmplitudeDeviceId() {
  const amplitudeDeviceId = getAmplitudeDeviceId()

  if (isInstanceDisabled() || !amplitudeDeviceId) {
    return
  }

  instance
    .getUser()
    ?.setCustomUserAttribute('amplitude_device_id', amplitudeDeviceId)

  const localStorage = useLocalStorage()
  localStorage.setItem('amplitudeDeviceId', amplitudeDeviceId)
}

const setGoogleAdCustomAttributes = ({
  hasUserConsent,
}: {
  hasUserConsent: boolean
}) => {
  if (isInstanceDisabled()) {
    return
  }

  const userHandle = instance.getUser()

  userHandle?.setCustomUserAttribute('$google_ad_user_data', hasUserConsent)
  userHandle?.setCustomUserAttribute(
    '$google_ad_personalization',
    hasUserConsent,
  )
}

export function setUserContext({ crmId }: { crmId?: string }) {
  if (isInstanceDisabled()) {
    return
  }

  if (crmId) {
    instance.changeUser(crmId)
  }

  if (!hasStoredAmplitudeDeviceId()) {
    setAmplitudeDeviceId()
  }
}

export function registerBrazeServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      void navigator.serviceWorker.register(SERVICE_WORKER_LOCATION)
    })
  }
}

export const sendEvent = (
  eventName: string,
  payload: Record<string, unknown>,
) => {
  if (isInstanceDisabled()) {
    return
  }

  instance.logCustomEvent(eventName, payload)
}

type InitBrazeParameters = {
  apiKey: string
  isOptInCookieNeeded: boolean
}

export const initBraze = async ({
  apiKey,
  isOptInCookieNeeded,
}: InitBrazeParameters) => {
  instance = await import('@braze/web-sdk')

  const areNeededCookiesAccepted =
    getBrowserCookie(COOKIES.userExperience) === 'true' &&
    getBrowserCookie(COOKIES.advertising) === 'true'

  const optIn = isOptInCookieNeeded ? areNeededCookiesAccepted : true

  if (!optIn) {
    instance.disableSDK()
  } else {
    instance.enableSDK()
  }

  const initOptions = {
    baseUrl: SDK_ENDPOINT,
    enableLogging: false,
    safariWebsitePushId: BRAZE_SAFARI_PUSH_ID,
    serviceWorkerLocation: SERVICE_WORKER_LOCATION,
  }

  instance.initialize(apiKey, initOptions)

  return instance
}

// called on GDPR update event
export const setOptIn = (optIn: boolean) => {
  if (!isInstanceAvailable()) {
    return
  }

  if (optIn) {
    instance.enableSDK()
    setGoogleAdCustomAttributes({ hasUserConsent: optIn })
  } else {
    setGoogleAdCustomAttributes({ hasUserConsent: optIn })
    instance.requestImmediateDataFlush()
    instance.disableSDK()
  }

  // We're setting a cookie that will be used to evaluate if we need
  // to open the cookie modal to request users' consent for new Google consent policy
  // We're using a cookie because the logic is done server-side where there's no localStorage
  // The value of the cookie ('true') does not really matter, we'll check for its presence
  // see https://www.google.com/about/company/user-consent-policy/
  // see also https://www.braze.com/docs/partners/canvas_steps/google_audience_sync#collecting-consent-for-eea-and-uk-end-users)

  const cookieRef = useCookie(
    BRAZE_CONSENT_COOKIE,
    createCookieConfig(GDPR_COOKIE_DURATION_IN_MONTHS),
  )
  cookieRef.value = 'true'
}

export function getInstance(): BrazeInstance | null {
  if (!isBrowser() || isInstanceDisabled()) {
    return null
  }

  return instance
}
